import { type FC, useCallback, useEffect, useMemo, useState } from 'react'
import { type InviteResponse } from '../../store/auth/auth'
import { type RoleType } from '../../models/Role'
import { AssignSeatModal } from './AssignSeatModal'
import { BuySeatModal } from './BuySeatModal'
import { RequestedSeatGroup } from './RequestedSeatGroup'
import { useSeatPurchaseGate } from './seatPurchaseGate'
import { RevokeRequestModal } from './RevokeRequestModal'
import { type RequestedGroupKey } from './requestedGroups'
import { useRequestedSeatGroups } from './useRequestedSeatGroups'

type ActiveModal =
  | { kind: 'assign'; invite: InviteResponse; seatType: RoleType; freeSeats: number }
  | { kind: 'buy'; invite: InviteResponse; seatType: RoleType }
  | { kind: 'revoke'; invite: InviteResponse }
  | null

export interface RequestedSeatGroupsProps {
  pages: Record<RequestedGroupKey, number>
  perPage: number
  namePattern: string | null
  roleGroupNames: string[]
  buySeatFor?: string | null
  onBuySeatForHandled?: () => void
  onChangePage: (key: RequestedGroupKey, page: number) => void
  onChangePerPage: (perPage: number, page: number) => void
}

export const RequestedSeatGroups: FC<RequestedSeatGroupsProps> = ({
  pages,
  perPage,
  namePattern,
  roleGroupNames,
  buySeatFor,
  onBuySeatForHandled,
  onChangePage,
  onChangePerPage,
}) => {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null)
  const { groups, refreshSeats } = useRequestedSeatGroups({ pages, perPage, namePattern, roleGroupNames })
  const canBuy = useSeatPurchaseGate()

  const buyFromLink = useMemo<ActiveModal>(() => {
    if (!buySeatFor || !canBuy) {
      return null
    }
    const match = groups
      .flatMap((group) => group.rows.map((groupRow) => ({ invite: groupRow.invite, seatType: group.seatType })))
      .find((candidate) => candidate.invite.id === buySeatFor)
    return match ? { kind: 'buy', invite: match.invite, seatType: match.seatType } : null
  }, [buySeatFor, canBuy, groups])

  useEffect(() => {
    if (!buySeatFor || buyFromLink) {
      return
    }
    if (!groups.some((group) => group.isFetching)) {
      onBuySeatForHandled?.()
    }
  }, [buySeatFor, buyFromLink, groups, onBuySeatForHandled])

  const shownModal = activeModal ?? buyFromLink

  const closeModal = () => {
    if (buySeatFor) {
      onBuySeatForHandled?.()
    }
    setActiveModal(null)
  }
  const openAssignModal = useCallback(
    (invite: InviteResponse, seatType: RoleType, freeSeats: number) => setActiveModal({ kind: 'assign', invite, seatType, freeSeats }),
    [],
  )
  const openBuyModal = useCallback((invite: InviteResponse, seatType: RoleType) => setActiveModal({ kind: 'buy', invite, seatType }), [])
  const openRevokeModal = useCallback((invite: InviteResponse) => setActiveModal({ kind: 'revoke', invite }), [])
  const changePerPage = useCallback((nextPerPage: number) => onChangePerPage(nextPerPage, 1), [onChangePerPage])

  return (
    <div>
      {shownModal?.kind === 'assign' ? (
        <AssignSeatModal
          invite={shownModal.invite}
          seatType={shownModal.seatType}
          freeSeats={shownModal.freeSeats}
          onHide={closeModal}
          onSeatsChanged={refreshSeats}
        />
      ) : null}
      {shownModal?.kind === 'buy' ? (
        <BuySeatModal invite={shownModal.invite} seatType={shownModal.seatType} onHide={closeModal} onSeatsChanged={refreshSeats} />
      ) : null}
      {shownModal?.kind === 'revoke' ? <RevokeRequestModal invite={shownModal.invite} onHide={closeModal} /> : null}
      {groups.map((group) => (
        <RequestedSeatGroup
          key={group.key}
          group={group}
          perPage={perPage}
          canBuy={canBuy}
          onAssignSeat={openAssignModal}
          onBuySeat={openBuyModal}
          onRevokeRequest={openRevokeModal}
          onChangePage={onChangePage}
          onChangeRowsPerPage={changePerPage}
        />
      ))}
    </div>
  )
}
